import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sendPasswordlessCode, verifyPasswordlessCode } from '@core/auth/authApi';
import { ApiError, generateFlowId, setFlowId } from '@core/api';

type Step = 'initial' | 'email' | 'verification';

const RESEND_COOLDOWN = 60;

interface UsePasswordlessAuthOptions {
  /** Restaurant subdomain prefix for the API call */
  restaurant: string;
  /** Called after successful verification with token + email + flowId */
  onSuccess: (token: string, email: string, flowId: string) => Promise<void> | void;
}

export function usePasswordlessAuth({ restaurant, onSuccess }: UsePasswordlessAuthOptions) {
  const { t } = useTranslation('auth');

  const [step, setStep] = useState<Step>('initial');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      cooldownRef.current = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [resendCooldown]);

  const startCooldown = useCallback(() => {
    setResendCooldown(RESEND_COOLDOWN);
  }, []);

  const isValidEmail = useCallback(
    (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
    [],
  );

  const goToEmail = useCallback(() => {
    setStep('email');
  }, []);

  const goBack = useCallback(() => {
    setError(null);
    if (step === 'email') {
      setStep('initial');
    } else if (step === 'verification') {
      setOtp('');
      setStep('email');
    }
  }, [step]);

  const sendCode = useCallback(async () => {
    if (!email || !isValidEmail(email)) {
      setError(t('SIGNINUP_EMAIL_INCORRECT_FORMAT'));
      return;
    }
    setError(null);
    setIsSending(true);
    try {
      await sendPasswordlessCode({ email, restaurant });
      startCooldown();
      setStep('verification');
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(t('MAGIC_LINK_TOO_MANY_ATTEMPTS'));
      } else {
        setError(t('MAGIC_LINK_GENERIC_ERROR'));
      }
    } finally {
      setIsSending(false);
    }
  }, [email, restaurant, t, startCooldown, isValidEmail]);

  const resendCode = useCallback(async () => {
    if (resendCooldown > 0) return;
    setError(null);
    setIsSending(true);
    try {
      await sendPasswordlessCode({ email, restaurant });
      startCooldown();
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError(t('MAGIC_LINK_TOO_MANY_ATTEMPTS'));
      } else {
        setError(t('MAGIC_LINK_GENERIC_ERROR'));
      }
    } finally {
      setIsSending(false);
    }
  }, [email, restaurant, resendCooldown, t, startCooldown]);

  const runVerify = useCallback(
    async (token: string, emailForPayload?: string) => {
      setError(null);
      setIsVerifying(true);
      const flowId = generateFlowId();
      setFlowId(flowId);
      try {
        const params = emailForPayload ? { token, email: emailForPayload } : { token };
        const result = await verifyPasswordlessCode(params);
        await onSuccess(result.token, result.email, flowId);
      } catch (err) {
        if (err instanceof ApiError) {
          if (err.status === 400) {
            setError(t('MAGIC_LINK_INVALID_CODE'));
          } else if (err.status === 429) {
            setError(t('MAGIC_LINK_TOO_MANY_ATTEMPTS'));
          } else {
            setError(t('MAGIC_LINK_GENERIC_ERROR'));
          }
        } else {
          setError(t('MAGIC_LINK_GENERIC_ERROR'));
        }
        setOtp('');
      } finally {
        setIsVerifying(false);
      }
    },
    [onSuccess, t],
  );

  const verify = useCallback(
    async (code: string) => {
      if (code.length !== 6) return;
      await runVerify(code, email);
    },
    [email, runVerify],
  );

  const verifyToken = useCallback(
    async (token: string) => {
      if (!token) return;
      await runVerify(token);
    },
    [runVerify],
  );

  const handleOtpChange = useCallback(
    (value: string) => {
      setOtp(value);
      if (value.length === 6) {
        verify(value);
      }
    },
    [verify],
  );

  return {
    step,
    email,
    setEmail: (value: string) => {
      setEmail(value);
      setError(null);
    },
    otp,
    handleOtpChange,
    isSending,
    isVerifying,
    error,
    resendCooldown,
    goToEmail,
    goBack,
    sendCode,
    resendCode,
    verifyToken,
    isValidEmail,
  };
}
